import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Image, Platform } from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/RestyleIcon';

interface MemberProps {
  user: {
    id: string;
    avatar?: { url: string };
    name: string;
  };
}

const Members = ({ item }: { item: MemberProps }) => {
  return (
    <Box m='xxs' flexDirection='row'>
      <Box marginHorizontal='xxs'>
        {item?.user?.avatar?.url ? (
          <Image
            style={{
              width: 24,
              height: 24,
              borderRadius: 30,
            }}
            defaultSource={require('../../../../assets/appIcon.png')}
            source={{ uri: item?.user?.avatar?.url }}
          />
        ) : (
          <Avatar
            onPress={() => {}}
            id={item?.user?.id}
            avatar={item?.user?.avatar}
            label={item?.user?.name}
            size='xs'
          />
        )}
      </Box>
      <Text color='textPrimary' fontWeight='500' fontSize={14}>
        {item?.user?.name}
      </Text>
    </Box>
  );
};

const getStatus = (status: string) => {
  return status
    ?.split('_')
    .map(
      (word: string) =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');
};

const getAssignee = (
  member: Array<{ role: string; user: { firstName: string } }>
) => {
  return member?.filter(
    (member: { role: string; user: { firstName: string } }) =>
      member?.role === 'ASSIGNEE'
  );
};

const priorityIcon = (priority: string) => {
  return priority === 'LOW' ? (
    <Box
      width={Platform.OS === 'web' ? 'auto' : 30}
      alignSelf='flex-start'
      marginHorizontal={Platform.OS === 'web' ? 'l' : 'xxs'}>
      <Box marginRight='l' />
    </Box>
  ) : (
    <Box
      width={Platform.OS === 'web' ? 'auto' : 30}
      alignSelf='flex-start'
      marginHorizontal={Platform.OS === 'web' ? 'l' : 'xxs'}>
      {priority === 'HIGH' && (
        <Icon name='StatusHigh' variant='l' color='textPrimary' />
      )}
      {priority === 'MEDIUM' && (
        <Icon name='StatusMedium' variant='l' color='textPrimary' />
      )}
    </Box>
  );
};

const RenderItem = ({
  item,
}: {
  item: TaskSectionProps['tasks']['listProjectReportTasks'][number];
}) => {
  const dueDate = item?.dueDate ? format(item?.dueDate, 'MMM dd, yyyy') : '';
  const assignee = getAssignee(item?.members);

  const status = getStatus(item?.status);

  return (
    <Box key={item?.name}>
      <Box marginVertical='xxs' justifyContent='center'>
        <Box
          flexDirection='row'
          marginHorizontal={Platform.OS === 'web' ? 'l' : 'xxs'}
          justifyContent='space-between'>
          <Box width='auto' flex={1}>
            <Text
              variant='webBodySecondary'
              textAlign='left'
              color='textPrimary'
              fontWeight='400'
              fontSize={Platform.OS === 'web' ? 14 : 12}>
              {item?.name}
            </Text>
          </Box>
          <Box flexDirection='row'>
            <Box
              width={Platform.OS === 'web' ? 130 : 40}
              marginHorizontal={Platform.OS === 'web' ? 'l' : 'xs'}>
              {assignee?.map(
                (assignee: { role: string; user: { firstName: string } }) => (
                  <Text
                    key={assignee?.user?.firstName}
                    textAlign='center'
                    color='textPrimary'
                    fontWeight='400'
                    fontSize={Platform.OS === 'web' ? 14 : 12}>
                    {assignee?.user?.firstName}
                  </Text>
                )
              )}
            </Box>
            <Box
              width={Platform.OS === 'web' ? 90 : 80}
              marginHorizontal={Platform.OS === 'web' ? 'm' : 'xxs'}>
              <Text
                textAlign='center'
                color='textPrimary'
                fontWeight='400'
                fontSize={Platform.OS === 'web' ? 14 : 12}>
                {dueDate}
              </Text>
            </Box>
            <Box
              width={Platform.OS === 'web' ? 100 : 70}
              marginHorizontal={Platform.OS === 'web' ? 'l' : 'xxs'}>
              <Text
                textAlign='center'
                color='textPrimary'
                fontWeight='400'
                fontSize={Platform.OS === 'web' ? 14 : 12}>
                {status}
              </Text>
              {item?.completedAt && (
                <Text
                  textAlign='center'
                  color='grey05'
                  fontWeight='400'
                  fontSize={Platform.OS === 'web' ? 12 : 10}>
                  {format(item?.completedAt, 'MMM dd, yyyy')}
                </Text>
              )}
            </Box>
            {priorityIcon(item?.priority)}
          </Box>
        </Box>
      </Box>
      <Box marginVertical='xxs' style={styles.horizonalLine}></Box>
    </Box>
  );
};

interface TaskSectionProps {
  tasks: {
    listProjectReportTasks: Array<{
      name: string;
      dueDate: Date;
      members: Array<{ role: string; user: { firstName: string } }>;
      status: string;
      completedAt?: Date;
      priority: string;
    }>;
  };
}

const TaskSection = ({ tasks }: TaskSectionProps) => {
  return (
    <Box>
      {tasks?.listProjectReportTasks?.map((item, index) => (
        <RenderItem item={item} index={index} key={item?.name} />
      ))}
    </Box>
  );
};

const TaskSectionHeader = () => {
  const { t } = useTranslation();
  return (
    <Box justifyContent='center' height={30} style={styles.taskSectionHeader}>
      <Box
        flexDirection='row'
        marginHorizontal={Platform.OS === 'web' ? 's' : 'xxs'}
        justifyContent='space-between'>
        <Box>
          <Text
            style={styles.textTaskSectionHeader}
            color='textPrimary'
            fontWeight='500'
            fontSize={10}>
            {t('models:projects.report.overview.taskSection.tasks')}
          </Text>
        </Box>
        <Box flexDirection='row'>
          <Box marginHorizontal={Platform.OS === 'web' ? 'xxl' : 'm'}>
            <Text
              style={styles.textTaskSectionHeader}
              color='textPrimary'
              fontWeight='500'
              fontSize={10}>
              {t('models:projects.report.overview.taskSection.assignee')}
            </Text>
          </Box>
          <Box
            alignSelf='center'
            marginHorizontal={Platform.OS === 'web' ? 'xxl' : 'm'}>
            <Text
              style={styles.textTaskSectionHeader}
              color='textPrimary'
              fontWeight='500'
              fontSize={10}>
              {t('models:projects.report.overview.taskSection.dueDate')}
            </Text>
          </Box>
          <Box marginHorizontal={Platform.OS === 'web' ? 'xxl' : 's'}>
            <Text
              style={styles.textTaskSectionHeader}
              color='textPrimary'
              fontWeight='500'
              fontSize={10}>
              {t('models:projects.report.overview.taskSection.status')}
            </Text>
          </Box>
          <Box marginHorizontal={Platform.OS === 'web' ? 'l' : 'none'}>
            <Text
              style={styles.textTaskSectionHeader}
              color='textPrimary'
              fontWeight='500'
              fontSize={10}>
              {t('models:projects.report.overview.taskSection.priority')}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

interface OverviewData {
  name: string;
  description?: string;
  creator?: {
    id: string;
    name: string;
    avatar?: { url: string };
  };
  teamName?: string;
  address?: string;
  members?: Array<{
    user: {
      id: string;
      avatar?: { url: string };
      name: string;
    };
  }>;
}

export const Overview = (props: {
  data: OverviewData;
  tasks: TaskSectionProps['tasks'];
  teamName: '';
}) => {
  const { t } = useTranslation();
  const overviewData = props?.data;
  const tasks = props?.tasks;
  const teamName = props?.teamName;

  return (
    <Box marginHorizontal='s'>
      <Box alignItems='flex-start' marginVertical='m'>
        <Text
          variant='heading1'
          color='textPrimary'
          fontWeight='600'
          fontSize={28}
          textAlign='left'
          marginVertical='xs'>
          {overviewData?.name}
        </Text>
        <Text
          color='grey04'
          fontWeight='600'
          fontSize={16}
          marginBottom='xs'
          textAlign='left'>
          {teamName ?? ''}
        </Text>

        <Text
          variant='labelLarge'
          color='textPrimary'
          fontWeight='700'
          fontSize={18}
          marginVertical='xs'
          textAlign='left'>
          Project Description
        </Text>
        {overviewData?.description && (
          <Text
            variant='body'
            color='textPrimary'
            fontWeight='400'
            fontSize={16}
            textAlign='left'>
            {overviewData?.description}
          </Text>
        )}
      </Box>
      <Box
        justifyContent={Platform.OS === 'web' ? 'space-around' : 'flex-start'}
        flexDirection='row'>
        <Box flexDirection='row'>
          <Box m='m'>
            {overviewData?.creator?.avatar?.url ? (
              <Image
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 30,
                }}
                source={{ uri: overviewData?.creator?.avatar?.url }}
              />
            ) : (
              <Avatar
                onPress={() => {}}
                id={overviewData?.creator?.id}
                avatar={null}
                label={overviewData?.creator?.name}
                size='medium'
              />
            )}
          </Box>
          <Box>
            <Text
              variant='labelSmall'
              color='textPrimary'
              fontWeight='700'
              fontSize={14}>
              {t('models:projects.report.overview.projectDescription.title')}
            </Text>
            <Text
              variant='labelSmall'
              color='textPrimary'
              fontWeight='500'
              fontSize={14}>
              {overviewData?.creator?.name}
            </Text>
            <Text
              variant='labelSmall'
              color='textPrimary'
              fontWeight='500'
              fontSize={14}>
              {overviewData?.team?.name}
            </Text>
          </Box>
        </Box>
        <Box marginHorizontal='m' style={styles.line}></Box>
        {/** Added platform specific styles as it behaves differently */}
        <Box flex={Platform.OS === 'web' ? -1 : 1}>
          <Text
            variant='labelSmall'
            color='textPrimary'
            fontWeight='700'
            fontSize={14}>
            {t('models:projects.report.overview.projectDescription.address')}
          </Text>
          <Text
            variant='labelSmall'
            color='textPrimary'
            fontWeight='500'
            fontSize={14}>
            {overviewData?.address}
          </Text>
        </Box>
      </Box>
      <Box marginVertical='m'>
        <Text
          variant='labelLarge'
          color='textPrimary'
          fontWeight='700'
          fontSize={18}
          marginVertical='xs'
          textAlign='left'>
          {t('models:projects.report.overview.memberSection.title')}
        </Text>
      </Box>
      <Box flexWrap='wrap' flexDirection='row'>
        {overviewData?.members?.map((item, index) => (
          <Members key={index} item={item} />
        ))}
      </Box>
      <Box marginVertical='m'>
        <Text
          variant='labelLarge'
          color='textPrimary'
          fontWeight='700'
          fontSize={18}
          marginVertical='xs'
          textAlign='left'>
          {t('models:projects.report.overview.taskOverview.title')}
        </Text>
      </Box>
      <Box justifyContent='center'>
        <TaskSectionHeader />
        <TaskSection tasks={tasks} />
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
  },
  line: {
    width: 1,
    height: '100%',
    backgroundColor: '#E8E8E8',
  },
  horizonalLine: {
    height: 1,
    width: '100%',
    backgroundColor: '#E8E8E8',
  },
  textTaskSectionHeader: {
    textTransform: 'uppercase',
    color: '#828282',
  },
  taskSectionHeader: {
    backgroundColor: '#F7F8FA80',
    opacity: 2,
    borderRadius: 5,
  },
});
